<template>
  <div class="content general-testing-test-page">
    <div class="content-header">
      <button class="content-header__exit" @click="logout">Выйти</button>
      <RouterLink :to="{ name: 'general-testing-block', params: { id: route.params.blockId } }" class="content-header__close">
        <i class="fas fa-times"></i>
      </RouterLink>
    </div>

    <div class="content-main">
      <div class="content-main_desc">
        <div v-if="isLoading" class="general-testing-state">Загрузка...</div>

        <template v-else>
          <div class="test-hero">
            <div>
              <div class="test-hero__eyebrow">Тест ГИМС</div>
              <h1 class="test-hero__title">{{ testTitle }}</h1>
              <p class="test-hero__subtitle">Ответы проверяются сразу после выбора. После фиксации варианта изменить его уже нельзя.</p>
            </div>
            <div class="test-hero__stats">
              <div class="test-hero__stat"><span>Всего вопросов</span><strong>{{ questions.length }}</strong></div>
              <div class="test-hero__stat"><span>Заполнено</span><strong>{{ answeredCount }}</strong></div>
              <div class="test-hero__stat"><span>Время</span><strong>{{ elapsedLabel }}</strong></div>
            </div>
          </div>

          <div v-if="result" class="test-result" :class="result.success ? 'test-result--success' : 'test-result--warning'">
            <div>
              <div class="test-result__eyebrow">Результат</div>
              <div class="test-result__score">{{ result.score }}%</div>
              <div class="test-result__summary">{{ result.success ? 'Тест пройден идеально' : 'Результат сохранён, ниже можно посмотреть ошибки и пояснения' }}</div>
            </div>
            <div class="test-result__stats">
              <div><span>Всего</span><strong>{{ result.counts?.all ?? 0 }}</strong></div>
              <div><span>Верно</span><strong>{{ result.counts?.right ?? 0 }}</strong></div>
              <div><span>Частично</span><strong>{{ result.counts?.part ?? 0 }}</strong></div>
              <div><span>Неверно</span><strong>{{ result.counts?.wrong ?? 0 }}</strong></div>
            </div>
          </div>

          <div v-if="questions.length" class="test-layout">
            <aside class="test-sidebar">
              <RouterLink :to="{ name: 'general-testing-block', params: { id: route.params.blockId } }" class="test-sidebar__back">← К списку тестов</RouterLink>
              <div class="test-sidebar__panel">
                <div class="test-sidebar__panel-label">Прогресс</div>
                <div class="test-sidebar__progress-value">{{ answeredCount }} / {{ questions.length }}</div>
                <div class="test-sidebar__progress-bar"><span :style="{ width: `${progressPercent}%` }"></span></div>
                <div class="test-sidebar__grid">
                  <button
                    v-for="(question, index) in questions"
                    :key="question.id"
                    type="button"
                    class="test-sidebar__dot"
                    :class="dotClass(question)"
                    @click="scrollToQuestion(question.id)"
                  >{{ index + 1 }}</button>
                </div>
                <div class="test-sidebar__hint">{{ locked ? 'После проверки можно увидеть правильные ответы и пояснения.' : 'Вопрос фиксируется сразу после выбора ответа.' }}</div>
              </div>
              <button
                v-if="!locked"
                type="submit"
                form="general-testing-form"
                class="button primary wide test-sidebar__submit"
                :disabled="isSubmitting"
              >{{ isSubmitting ? 'Отправляем...' : 'Завершить тестирование' }}</button>
              <button v-else type="button" class="button secondary wide test-sidebar__submit" @click="unlockForRetry">Пройти ещё раз</button>
            </aside>

            <form id="general-testing-form" class="test-form" @submit.prevent="submitTest">
              <div v-if="generalError" class="test-form__error">{{ generalError }}</div>
              <section
                v-for="(question, index) in questions"
                :key="question.id"
                :id="`question-${question.id}`"
                class="test-card"
                :class="cardClass(question)"
              >
                <div class="test-card__head">
                  <div>
                    <div class="test-card__index">Вопрос {{ index + 1 }}</div>
                    <h2 class="test-card__title">{{ question.name }}</h2>
                  </div>
                  <div class="test-card__head-side">
                    <button v-if="canShowExplanation(question)" type="button" class="test-card__help" @click="openExplanation(question)">?</button>
                  </div>
                </div>

                <div class="test-card__body">
                  <div>
                    <p v-if="question.description" class="test-card__description">{{ question.description }}</p>

                    <div class="test-card__answers">
                      <template v-if="question.type === 'one' || question.type === 'multi'">
                        <label v-for="answer in question.answers" :key="answer.id" class="answer-choice" :class="choiceClass(question, answer.id)">
                          <input
                            v-if="question.type === 'one'"
                            :name="`question-${question.id}`"
                            type="radio"
                            :value="answer.id"
                            :checked="isSelected(question, answer.id)"
                            :disabled="isQuestionLocked(question)"
                            @change="submitSingleChoice(question, answer.id)"
                          >
                          <input
                            v-else
                            type="checkbox"
                            :value="answer.id"
                            :checked="isSelected(question, answer.id)"
                            :disabled="isQuestionLocked(question) || isMultiChoiceDisabled(question, answer.id)"
                            @change="toggleMultiAnswer(question, answer.id, $event.target.checked)"
                          >
                          <span class="answer-choice__control"></span>
                          <span class="answer-choice__text">{{ answer.answer }}</span>
                          <span v-if="isQuestionChecked(question) && isCorrectAnswer(question, answer.id)" class="answer-choice__mark answer-choice__mark--correct">Верно</span>
                          <span v-else-if="isQuestionChecked(question) && isWrongSelectedAnswer(question, answer.id)" class="answer-choice__mark answer-choice__mark--wrong">Ваш ответ</span>
                        </label>
                      </template>

                      <template v-else-if="question.type === 'compare'">
                        <div class="compare-grid">
                          <div v-for="answer in question.answers" :key="answer.id" class="compare-row" :class="compareClass(question, answer)">
                            <label class="compare-row__label" :for="`q${question.id}_${answer.id}`">{{ answer.answer }}</label>
                            <div class="compare-row__field">
                              <select
                                class="compare-row__select"
                                :id="`q${question.id}_${answer.id}`"
                                :value="getCompareValue(question, answer.id)"
                                :disabled="isQuestionLocked(question)"
                                @change="updateCompareAnswer(question, answer.id, $event.target.value)"
                              >
                                <option value="" disabled>Выберите вариант</option>
                                <option v-for="other in question.compare_answers" :key="other" :value="other">{{ other }}</option>
                              </select>
                              <span class="compare-row__chevron"><i class="fas fa-chevron-down"></i></span>
                            </div>
                            <div v-if="isQuestionChecked(question) && answer.answer_compare" class="compare-row__right-answer">Правильно: {{ answer.answer_compare }}</div>
                          </div>
                        </div>
                      </template>

                      <div v-else-if="question.type === 'text'" class="text-answer" :class="textClass(question)">
                        <label class="text-answer__label" :for="`q${question.id}`">Ваш ответ</label>
                        <textarea
                          class="text-answer__field"
                          :id="`q${question.id}`"
                          :value="getTextAnswer(question)"
                          :disabled="isQuestionLocked(question)"
                          placeholder="Введите ответ в свободной форме"
                          @input="updateTextAnswer(question, $event.target.value)"
                          @blur="finalizeTextAnswer(question)"
                        ></textarea>
                      </div>

                      <div v-if="questionError(index)" class="question-error">{{ questionError(index) }}</div>
                    </div>
                  </div>

                  <div v-if="question.image_url" class="test-card__media"><img :src="question.image_url" alt="Иллюстрация к вопросу"></div>
                </div>
              </section>

              <button
                v-if="!locked"
                type="submit"
                class="button primary wide test-form__mobile-submit"
                :disabled="isSubmitting"
              >{{ isSubmitting ? 'Отправляем...' : 'Завершить тестирование' }}</button>
            </form>
          </div>

          <div v-else class="general-testing-state">В этом тесте пока нет вопросов</div>
        </template>
      </div>
    </div>

    <BaseModal :show="!!explanationQuestion" title="Пояснение к ответу">
      <template #description-slot>
        <div class="explanation-modal">
          <div class="explanation-modal__title">{{ explanationQuestion?.name }}</div>
          <div class="explanation-modal__text">{{ explanationQuestion?.right_comment }}</div>
        </div>
      </template>
      <button class="button primary" @click="closeExplanation">Понятно</button>
    </BaseModal>
  </div>
</template>

<script>
import { computed, inject, onMounted, onUnmounted, ref, watch } from "@vue/runtime-core";
import { useRoute } from "vue-router";
import BaseModal from "@/components/common/BaseModal";
import { getGeneralTestingTest, getGeneralTestingTestResult, sendGeneralTestingTest } from "@/data/requests";

const RECENT_TESTING_KEY = "cmp_recent_testing";
const GENERAL_PROGRESS_PREFIX = "cmp_general_testing_progress:";

const normalizeQuestion = (question) => ({
  ...question,
  name: question?.name ?? question?.title ?? "Вопрос",
  description: question?.description ?? question?.text ?? "",
  answers: question?.answers ?? [],
  compare_answers: question?.compare_answers ?? question?.compareAnswers ?? [],
  right_comment: question?.right_comment ?? "",
});

const buildForm = (questions) => questions.reduce((acc, question) => {
  acc[question.id] = { question_id: question.id, type: question.type };
  if (question.type === "compare") acc[question.id].answer_compare = {};
  else if (question.type === "multi") acc[question.id].answer_ids = [];
  else if (question.type === "one") acc[question.id].answer_id = null;
  else if (question.type === "text") acc[question.id].answer_text = "";
  return acc;
}, {});

const normalizeCompareAnswer = (id, value) => ({ id: Number.isNaN(Number(id)) ? id : Number(id), answer_compare: value });

function readStorage(key) {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    return null;
  }
}

function writeStorage(key, value) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(key, JSON.stringify(value));
}

function removeStorage(key) {
  if (typeof window === "undefined") return;
  window.localStorage.removeItem(key);
}

export default {
  components: { BaseModal },
  setup() {
    const logout = inject("logout");
    const token = inject("token");
    const route = useRoute();
    const currentBlockId = ref(String(route.params.blockId ?? ""));
    const currentTestId = ref(String(route.params.testId ?? ""));
    const testData = ref(null);
    const questions = ref([]);
    const form = ref({});
    const formErrors = ref({});
    const generalError = ref("");
    const result = ref(null);
    const isLoading = ref(true);
    const isSubmitting = ref(false);
    const locked = ref(false);
    const activeQuestionId = ref(null);
    const explanationQuestion = ref(null);
    const questionLocks = ref({});
    const elapsedSeconds = ref(0);
    let timer = null;

    const currentPath = computed(() => `/general-testing/${currentBlockId.value}/test/${currentTestId.value}`);
    const progressStorageKey = computed(() => `${GENERAL_PROGRESS_PREFIX}${currentBlockId.value}:${currentTestId.value}`);
    const testTitle = computed(() => testData.value?.name ?? testData.value?.title ?? "Тест");
    const answeredCount = computed(() => questions.value.reduce((sum, question) => sum + (isAnswered(question) ? 1 : 0), 0));
    const progressPercent = computed(() => !questions.value.length ? 0 : Math.round((answeredCount.value / questions.value.length) * 100));
    const elapsedLabel = computed(() => `${Math.floor(elapsedSeconds.value / 60)}:${String(elapsedSeconds.value % 60).padStart(2, "0")}`);

    function saveRecentTesting() {
      writeStorage(RECENT_TESTING_KEY, {
        type: "general",
        path: currentPath.value,
        title: testTitle.value,
        savedAt: Date.now(),
      });
    }

    function clearRecentTesting() {
      const recent = readStorage(RECENT_TESTING_KEY);
      if (recent?.path === currentPath.value) removeStorage(RECENT_TESTING_KEY);
    }

    function ensureQuestionForm(question) {
      if (!form.value[question.id]) {
        form.value = {
          ...form.value,
          ...buildForm([question]),
        };
      }
    }

    function setQuestionStatus(questionId, status) {
      questions.value = questions.value.map((question) => (
        question.id === questionId ? { ...question, status } : question
      ));
    }

    function lockQuestion(questionId) {
      questionLocks.value = {
        ...questionLocks.value,
        [questionId]: true,
      };
    }

    function isQuestionLocked(question) {
      return locked.value || !!questionLocks.value[question.id];
    }

    function isQuestionChecked(question) {
      return locked.value || !!questionLocks.value[question.id];
    }

    function getRightAnswersCount(question) {
      return (question.answers ?? []).filter((answer) => !!answer.right).length;
    }

    function validateQuestionLocally(question) {
      const current = form.value[question.id];
      if (!current) return null;

      if (question.type === "one") {
        const selectedAnswer = (question.answers ?? []).find((answer) => String(answer.id) === String(current.answer_id));
        return selectedAnswer?.right ? "right" : "wrong";
      }

      if (question.type === "multi") {
        const selectedIds = (current.answer_ids ?? []).map(String).sort();
        const rightIds = (question.answers ?? []).filter((answer) => !!answer.right).map((answer) => String(answer.id)).sort();
        if (!selectedIds.length) return null;
        return JSON.stringify(selectedIds) === JSON.stringify(rightIds) ? "right" : "wrong";
      }

      if (question.type === "compare") {
        const chosen = current.answer_compare ?? {};
        const allFilled = (question.answers ?? []).every((answer) => !!chosen[answer.id]);
        if (!allFilled) return null;
        const isRight = (question.answers ?? []).every((answer) => chosen[answer.id] === answer.answer_compare);
        return isRight ? "right" : "wrong";
      }

      return null;
    }

    function restoreProgress() {
      const snapshot = readStorage(progressStorageKey.value);
      if (!snapshot || snapshot?.path !== currentPath.value) return;

      elapsedSeconds.value = Math.max(Number(snapshot.elapsedSeconds) || 0, 0);
      questionLocks.value = snapshot.questionLocks && typeof snapshot.questionLocks === "object" ? snapshot.questionLocks : {};

      const statuses = snapshot.statuses && typeof snapshot.statuses === "object" ? snapshot.statuses : {};
      if (Object.keys(statuses).length) {
        questions.value = questions.value.map((question) => ({ ...question, status: statuses[question.id] ?? question.status ?? null }));
      }

      questions.value.forEach((question) => {
        const saved = snapshot.form?.[question.id];
        if (!saved || !form.value[question.id]) return;

        if (question.type === "one") {
          form.value[question.id].answer_id = saved.answer_id ?? null;
        } else if (question.type === "multi") {
          form.value[question.id].answer_ids = Array.isArray(saved.answer_ids) ? saved.answer_ids : [];
        } else if (question.type === "text") {
          form.value[question.id].answer_text = saved.answer_text ?? "";
        } else if (question.type === "compare") {
          form.value[question.id].answer_compare = saved.answer_compare && typeof saved.answer_compare === "object" ? saved.answer_compare : {};
        }
      });

      if (questions.value.some((question) => question.id === snapshot.activeQuestionId)) {
        activeQuestionId.value = snapshot.activeQuestionId;
      }
    }

    function persistProgress() {
      if (isLoading.value || locked.value || !questions.value.length) return;
      const statuses = questions.value.reduce((acc, question) => {
        if (question.status) acc[question.id] = question.status;
        return acc;
      }, {});

      writeStorage(progressStorageKey.value, {
        path: currentPath.value,
        title: testTitle.value,
        elapsedSeconds: elapsedSeconds.value,
        activeQuestionId: activeQuestionId.value,
        questionLocks: questionLocks.value,
        statuses,
        form: form.value,
        savedAt: Date.now(),
      });
      saveRecentTesting();
    }

    function clearProgress() {
      removeStorage(progressStorageKey.value);
      clearRecentTesting();
    }

    function startTimer() {
      stopTimer();
      timer = setInterval(() => {
        elapsedSeconds.value += 1;
      }, 1000);
    }

    function stopTimer() {
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
    }

    function syncRouteIds() {
      currentBlockId.value = String(route.params.blockId ?? "");
      currentTestId.value = String(route.params.testId ?? "");
    }

    async function loadData() {
      syncRouteIds();
      stopTimer();
      isLoading.value = true;
      testData.value = null;
      questions.value = [];
      form.value = {};
      result.value = null;
      locked.value = false;
      explanationQuestion.value = null;
      questionLocks.value = {};
      formErrors.value = {};
      generalError.value = "";
      elapsedSeconds.value = 0;
      activeQuestionId.value = null;

      const response = await getGeneralTestingTest(token.value, currentBlockId.value, currentTestId.value);
      const payload = response?.test ?? response?.data ?? response;
      testData.value = payload;
      questions.value = (payload?.questions ?? []).map(normalizeQuestion);
      form.value = buildForm(questions.value);
      activeQuestionId.value = questions.value[0]?.id ?? null;
      restoreProgress();
      isLoading.value = false;
      startTimer();
    }

    function applyResult(resultData) {
      result.value = resultData;
      locked.value = true;
      questionLocks.value = {};
      questions.value = questions.value.map((question) => {
        const found = (resultData?.result ?? []).find((item) => item.question?.id === question.id);
        if (!found) return { ...question, status: null };
        return { ...normalizeQuestion(found.question), status: found.status, user_answer: found.user_answer };
      });

      questions.value.forEach((question) => {
        const userAnswer = question.user_answer;
        if (!userAnswer || !form.value[question.id]) return;
        if (question.type === "one") form.value[question.id].answer_id = userAnswer.answer_id;
        else if (question.type === "multi") form.value[question.id].answer_ids = userAnswer.answer_ids ?? [];
        else if (question.type === "text") form.value[question.id].answer_text = userAnswer.answer_text ?? "";
        else if (question.type === "compare") {
          const mapped = {};
          (userAnswer.answer_compare ?? []).forEach((item) => {
            mapped[item.id] = item.answer_compare;
          });
          form.value[question.id].answer_compare = mapped;
        }
      });
    }

    async function loadResult() {
      const resultData = await getGeneralTestingTestResult(token.value, currentBlockId.value, currentTestId.value);
      applyResult(resultData);
      clearProgress();
    }

    function submitSingleChoice(question, answerId) {
      if (isQuestionLocked(question)) return;
      ensureQuestionForm(question);
      form.value[question.id].answer_id = answerId;
      setQuestionStatus(question.id, validateQuestionLocally(question));
      lockQuestion(question.id);
    }

    function isMultiChoiceDisabled(question, answerId) {
      if (isQuestionLocked(question)) return true;
      if (isSelected(question, answerId)) return false;
      const current = form.value[question.id];
      const maxCount = getRightAnswersCount(question);
      return Array.isArray(current?.answer_ids) && maxCount > 0 && current.answer_ids.length >= maxCount;
    }

    function toggleMultiAnswer(question, answerId, checked) {
      if (isQuestionLocked(question)) return;
      ensureQuestionForm(question);

      const selectedIds = Array.isArray(form.value[question.id].answer_ids)
        ? [...form.value[question.id].answer_ids]
        : [];

      if (!checked) {
        form.value[question.id].answer_ids = selectedIds.filter((id) => String(id) !== String(answerId));
        return;
      }

      if (!selectedIds.map(String).includes(String(answerId))) {
        form.value[question.id].answer_ids = [...selectedIds, answerId];
      }

      if (form.value[question.id].answer_ids.length >= getRightAnswersCount(question)) {
        setQuestionStatus(question.id, validateQuestionLocally(question));
        lockQuestion(question.id);
      }
    }

    function updateCompareAnswer(question, answerId, value) {
      if (isQuestionLocked(question)) return;
      ensureQuestionForm(question);
      form.value[question.id].answer_compare = {
        ...form.value[question.id].answer_compare,
        [answerId]: value,
      };

      const allFilled = (question.answers ?? []).every((answer) => !!form.value[question.id].answer_compare?.[answer.id]);
      if (allFilled) {
        setQuestionStatus(question.id, validateQuestionLocally(question));
        lockQuestion(question.id);
      }
    }

    function finalizeTextAnswer(question) {
      if (isQuestionLocked(question)) return;
      ensureQuestionForm(question);
      if (!form.value[question.id]?.answer_text?.trim()) return;
      lockQuestion(question.id);
    }

    function isAnswered(question) {
      const current = form.value[question.id];
      if (!current) return false;
      if (question.type === "one") return current.answer_id !== null && current.answer_id !== undefined;
      if (question.type === "multi") return Array.isArray(current.answer_ids) && current.answer_ids.length > 0;
      if (question.type === "compare") return Object.values(current.answer_compare ?? {}).filter(Boolean).length > 0;
      if (question.type === "text") return !!current.answer_text?.trim();
      return false;
    }

    function isSelected(question, answerId) {
      const current = form.value[question.id];
      if (!current) return false;
      if (question.type === "one") return String(current.answer_id) === String(answerId);
      if (question.type === "multi") return (current.answer_ids ?? []).map(String).includes(String(answerId));
      return false;
    }

    function isCorrectAnswer(question, answerId) {
      return isQuestionChecked(question) && question.answers?.some((answer) => answer.id === answerId && !!answer.right);
    }

    function isWrongSelectedAnswer(question, answerId) {
      return isQuestionChecked(question) && isSelected(question, answerId) && !isCorrectAnswer(question, answerId);
    }

    function canShowExplanation(question) {
      return isQuestionChecked(question) && !!question.right_comment && question.status !== "right";
    }

    function getCompareValue(question, answerId) {
      return form.value[question.id]?.answer_compare?.[answerId] ?? "";
    }

    function getTextAnswer(question) {
      return form.value[question.id]?.answer_text ?? "";
    }

    function updateTextAnswer(question, value) {
      ensureQuestionForm(question);
      form.value[question.id].answer_text = value;
    }

    function openExplanation(question) {
      explanationQuestion.value = question;
    }

    function closeExplanation() {
      explanationQuestion.value = null;
    }

    function scrollToQuestion(questionId) {
      activeQuestionId.value = questionId;
      const element = document.getElementById(`question-${questionId}`);
      if (element) element.scrollIntoView({ behavior: "smooth", block: "start" });
    }

    function serializeAnswers() {
      return questions.value.map((question) => {
        const answer = form.value[question.id];
        if (question.type === "one") return { question_id: question.id, answer_id: answer.answer_id };
        if (question.type === "multi") return { question_id: question.id, answer_ids: answer.answer_ids };
        if (question.type === "text") return { question_id: question.id, answer_text: answer.answer_text };
        return {
          question_id: question.id,
          answer_compare: Object.entries(answer.answer_compare ?? {})
            .filter(([, value]) => value !== undefined && value !== null && value !== "")
            .map(([id, value]) => normalizeCompareAnswer(id, value)),
        };
      });
    }

    function questionError(index) {
      const keys = [
        `answers.${index}.question_id`,
        `answers.${index}.answer_id`,
        `answers.${index}.answer_ids`,
        `answers.${index}.answer_text`,
        `answers.${index}.answer_compare`,
      ];
      for (const key of keys) {
        if (formErrors.value[key]) return Array.isArray(formErrors.value[key]) ? formErrors.value[key][0] : formErrors.value[key];
      }
      return "";
    }

    async function submitTest() {
      if (locked.value || isSubmitting.value) return;
      isSubmitting.value = true;
      formErrors.value = {};
      generalError.value = "";
      const payload = { seconds: Math.max(elapsedSeconds.value, 1), answers: serializeAnswers() };
      const response = await sendGeneralTestingTest(token.value, currentBlockId.value, currentTestId.value, payload);
      isSubmitting.value = false;

      if (response.code === 422) {
        formErrors.value = response.data?.errors ?? {};
        generalError.value = response.data?.message ?? "Проверьте заполнение теста";
        return;
      }

      stopTimer();
      await loadResult();
      window.scrollTo({ top: 0, behavior: "smooth" });
    }

    function unlockForRetry() {
      result.value = null;
      formErrors.value = {};
      generalError.value = "";
      locked.value = false;
      explanationQuestion.value = null;
      questionLocks.value = {};
      elapsedSeconds.value = 0;
      form.value = buildForm(questions.value);
      questions.value = questions.value.map((question) => ({ ...question, status: null, user_answer: null }));
      activeQuestionId.value = questions.value[0]?.id ?? null;
      persistProgress();
      startTimer();
    }

    function dotClass(question) {
      return {
        "is-active": activeQuestionId.value === question.id,
        "is-success": question.status === "right",
        "is-failed": ["wrong", "part"].includes(question.status),
        "is-answered": isAnswered(question),
      };
    }

    function cardClass(question) {
      return {
        "test-card--with-image": question.image_url,
        "test-card--right": question.status === "right",
        "test-card--wrong": question.status === "wrong",
        "test-card--part": question.status === "part",
      };
    }

    function choiceClass(question, answerId) {
      return {
        "answer-choice--selected": isSelected(question, answerId),
        "answer-choice--radio": question.type === "one",
        "answer-choice--correct": isCorrectAnswer(question, answerId),
        "answer-choice--wrong": isWrongSelectedAnswer(question, answerId),
      };
    }

    function compareClass(question, answer) {
      const chosen = form.value[question.id]?.answer_compare?.[answer.id];
      return {
        "compare-row--correct": isQuestionChecked(question) && chosen && answer.answer_compare && chosen === answer.answer_compare,
        "compare-row--wrong": isQuestionChecked(question) && chosen && answer.answer_compare && chosen !== answer.answer_compare,
      };
    }

    function textClass(question) {
      return {
        "text-answer--correct": locked.value && question.status === "right",
        "text-answer--wrong": locked.value && ["wrong", "part"].includes(question.status),
      };
    }

    watch(form, () => {
      if (locked.value) return;
      const firstIncomplete = questions.value.find((question) => !isAnswered(question));
      activeQuestionId.value = firstIncomplete?.id ?? questions.value[questions.value.length - 1]?.id ?? null;
    }, { deep: true });

    watch(form, persistProgress, { deep: true });
    watch(questionLocks, persistProgress, { deep: true });
    watch(elapsedSeconds, persistProgress);
    watch(activeQuestionId, persistProgress);
    watch(() => [route.params.blockId, route.params.testId], loadData);

    onMounted(loadData);
    onUnmounted(() => {
      persistProgress();
      stopTimer();
    });

    return {
      logout,
      route,
      isLoading,
      isSubmitting,
      locked,
      testTitle,
      questions,
      form,
      result,
      generalError,
      answeredCount,
      progressPercent,
      elapsedLabel,
      activeQuestionId,
      explanationQuestion,
      isAnswered,
      isSelected,
      isCorrectAnswer,
      isWrongSelectedAnswer,
      isQuestionLocked,
      isQuestionChecked,
      submitSingleChoice,
      isMultiChoiceDisabled,
      toggleMultiAnswer,
      updateCompareAnswer,
      finalizeTextAnswer,
      getCompareValue,
      getTextAnswer,
      updateTextAnswer,
      canShowExplanation,
      openExplanation,
      closeExplanation,
      scrollToQuestion,
      questionError,
      submitTest,
      unlockForRetry,
      dotClass,
      cardClass,
      choiceClass,
      compareClass,
      textClass,
    };
  },
};
</script>

<style scoped lang="scss">
.content-main_desc { max-width: 1240px; width: 100%; }
.general-testing-state { margin-top: 24px; font-size: 16px; }
.test-hero { margin-top: 20px; padding: 32px; border-radius: 28px; color: #fff; display: grid; grid-template-columns: minmax(0, 2fr) minmax(260px, .9fr); gap: 24px; background: radial-gradient(circle at top left, rgba(255,255,255,.18), transparent 32%), linear-gradient(135deg, #0f4c81 0%, #0a7abf 48%, #11b4a6 100%); }
.test-hero__eyebrow { font-size: 13px; letter-spacing: .12em; text-transform: uppercase; opacity: .8; margin-bottom: 10px; }
.test-hero__title { font-size: 40px; line-height: 1.05; margin: 0 0 14px; }
.test-hero__subtitle { max-width: 680px; margin: 0; color: rgba(255,255,255,.86); line-height: 1.6; }
.test-hero__stats, .test-result__stats { display: grid; gap: 12px; }
.test-hero__stat, .test-result__stats div { padding: 16px 18px; border-radius: 18px; background: rgba(255,255,255,.14); }
.test-hero__stat span, .test-result__stats span { display: block; font-size: 12px; opacity: .8; margin-bottom: 8px; }
.test-hero__stat strong, .test-result__stats strong { font-size: 30px; line-height: 1; }
.test-result { margin-top: 18px; padding: 22px 24px; border-radius: 24px; display: grid; grid-template-columns: minmax(0,1.3fr) minmax(320px,1fr); gap: 24px; color: #fff; }
.test-result--success { background: linear-gradient(135deg, #08946f 0%, #18b88d 100%); }
.test-result--warning { background: linear-gradient(135deg, #d06a16 0%, #f09a2b 100%); }
.test-result__eyebrow { opacity: .78; text-transform: uppercase; letter-spacing: .08em; font-size: 12px; }
.test-result__score { font-size: 54px; line-height: 1; margin: 8px 0 10px; }
.test-layout { margin-top: 24px; display: grid; grid-template-columns: 290px minmax(0,1fr); gap: 24px; align-items: start; }
.test-sidebar { position: sticky; top: 20px; }
.test-sidebar__back { display: inline-block; margin-bottom: 14px; color: #0a6ca8; text-decoration: none; }
.test-sidebar__panel { background: linear-gradient(180deg, #f3fbff 0%, #eef4fb 100%); border: 1px solid #dbe8f5; border-radius: 24px; padding: 20px; }
.test-sidebar__panel-label { font-size: 12px; text-transform: uppercase; letter-spacing: .08em; color: #668399; }
.test-sidebar__progress-value { font-size: 30px; margin: 8px 0 16px; color: #16344d; }
.test-sidebar__progress-bar { height: 12px; border-radius: 999px; background: rgba(13,108,168,.12); overflow: hidden; }
.test-sidebar__progress-bar span { display: block; height: 100%; background: linear-gradient(90deg, #ff8a3d 0%, #0aa6d9 100%); }
.test-sidebar__grid { margin-top: 18px; display: grid; grid-template-columns: repeat(4, minmax(0,1fr)); gap: 10px; }
.test-sidebar__dot { border: none; border-radius: 14px; height: 44px; font-weight: 700; color: #5c7488; background: #fff; cursor: pointer; }
.test-sidebar__dot.is-active { outline: 2px solid #0a7abf; color: #0a7abf; }
.test-sidebar__dot.is-answered { background: #eef8ff; color: #0a7abf; box-shadow: inset 0 0 0 2px rgba(10, 122, 191, .28); }
.test-sidebar__dot.is-success { background: #dff6f2; color: #0b8f7f; box-shadow: inset 0 0 0 2px rgba(11, 143, 127, .24); }
.test-sidebar__dot.is-failed { background: #fff1ec; color: #d95f22; box-shadow: inset 0 0 0 2px rgba(217, 95, 34, .22); }
.test-sidebar__hint { margin-top: 16px; color: #557286; font-size: 13px; line-height: 1.55; }
.test-sidebar__submit { margin-top: 16px; }
.test-form { display: grid; gap: 20px; }
.test-form__mobile-submit { display: none; }
.test-form__error { padding: 16px 18px; border-radius: 18px; background: #fff2f4; border: 1px solid #ffd1d9; color: #b42343; }
.test-card { background: radial-gradient(circle at top right, rgba(10,122,191,.08), transparent 25%), #fff; border: 1px solid #e2ecf4; border-radius: 28px; padding: 28px; box-shadow: 0 18px 40px rgba(15,76,129,.08); }
.test-card--right { border-color: #8dd6be; }
.test-card--wrong, .test-card--part { border-color: #ffc9b3; }
.test-card__head { display: flex; justify-content: space-between; gap: 16px; align-items: flex-start; margin-bottom: 20px; }
.test-card__head-side { display: flex; align-items: center; gap: 10px; }
.test-card__help { width: 34px; height: 34px; border-radius: 50%; border: none; background: #ffe1cf; color: #c1551d; font-weight: 700; font-size: 18px; cursor: pointer; }
.test-card__index { font-size: 13px; text-transform: uppercase; letter-spacing: .08em; color: #6a8294; margin-bottom: 8px; }
.test-card__title { margin: 0; font-size: 30px; line-height: 1.1; color: #16344d; }
.test-card__body { display: grid; grid-template-columns: minmax(0,1fr) 300px; gap: 24px; }
.test-card__description { margin: 0 0 18px; color: #425b6f; line-height: 1.7; font-size: 16px; }
.test-card__answers { display: grid; gap: 14px; }
.answer-choice { position: relative; display: flex; align-items: center; gap: 14px; padding: 18px; border-radius: 20px; border: 1px solid #d9e6f2; background: #fbfdff; cursor: pointer; transition: border-color .2s ease, box-shadow .2s ease, background-color .2s ease, transform .2s ease; }
.answer-choice input { position: absolute; opacity: 0; pointer-events: none; }
.answer-choice__control { position: relative; width: 22px; min-width: 22px; height: 22px; border-radius: 8px; border: 2px solid #d5e4f0; background: #f1f7fb; box-sizing: border-box; }
.answer-choice__control::after { content: ""; position: absolute; top: 50%; left: 50%; width: 10px; height: 10px; transform: translate(-50%, -50%) scale(0); border-radius: 3px; background: #fff; transition: transform .18s ease; }
.answer-choice--radio .answer-choice__control { border-radius: 999px; }
.answer-choice--radio .answer-choice__control::after { border-radius: 999px; width: 8px; height: 8px; }
.answer-choice__text { flex: 1 1 auto; color: #233b4d; }
.answer-choice__mark { font-size: 12px; font-weight: 700; text-transform: uppercase; }
.answer-choice__mark--correct { color: #0b8f7f; }
.answer-choice__mark--wrong { color: #c1551d; }
.answer-choice--selected { border-color: #0a7abf; background: linear-gradient(180deg, #f4fbff 0%, #eaf5ff 100%); box-shadow: 0 0 0 2px rgba(10, 122, 191, .14), 0 14px 28px rgba(10, 122, 191, .10); transform: translateY(-1px); }
.answer-choice--selected .answer-choice__control { background: #0a7abf; border-color: #0a7abf; }
.answer-choice--selected .answer-choice__control::after { transform: translate(-50%, -50%) scale(1); }
.answer-choice--correct { border-color: #8dd6be; background: #effaf6; }
.answer-choice--correct .answer-choice__control { background: #16a07d; border-color: #16a07d; }
.answer-choice--correct .answer-choice__control::after { transform: translate(-50%, -50%) scale(1); }
.answer-choice--wrong { border-color: #ffb899; background: #fff4ef; }
.answer-choice--wrong .answer-choice__control { background: #d95f22; border-color: #d95f22; }
.answer-choice--wrong .answer-choice__control::after { transform: translate(-50%, -50%) scale(1); }
.compare-grid { display: grid; gap: 12px; }
.compare-row { display: grid; grid-template-columns: minmax(180px,1fr) minmax(0,1.4fr); gap: 14px; align-items: center; padding: 14px; border-radius: 18px; border: 1px solid #e0e9f1; background: #fbfdff; }
.compare-row--correct { border-color: #8dd6be; background: #effaf6; }
.compare-row--wrong { border-color: #ffb899; background: #fff4ef; }
.compare-row__field { position: relative; }
.compare-row__select { width: 100%; padding: 16px 44px 16px 16px; border-radius: 16px; border: 1px solid #d3e2ee; background: #f8fbfe; font: inherit; appearance: none; }
.compare-row__chevron { position: absolute; right: 16px; top: 50%; transform: translateY(-50%); color: #7c93a6; pointer-events: none; }
.compare-row__right-answer { grid-column: 1 / -1; color: #567085; font-size: 13px; }
.text-answer { border-radius: 18px; padding: 14px; border: 1px solid #d3e2ee; background: #fbfdff; }
.text-answer--correct { border-color: #8dd6be; background: #effaf6; }
.text-answer--wrong { border-color: #ffb899; background: #fff4ef; }
.text-answer__label { display: block; margin-bottom: 10px; color: #284559; font-weight: 600; }
.text-answer__field { width: 100%; min-height: 140px; padding: 18px; border-radius: 18px; border: 1px solid #d3e2ee; background: #fff; font: inherit; resize: vertical; }
.question-error { color: #b42343; font-size: 14px; }
.test-card__media { align-self: start; background: linear-gradient(180deg, #f7fbff 0%, #eef5fb 100%); border-radius: 24px; overflow: hidden; min-height: 220px; }
.test-card__media img { display: block; width: 100%; height: 100%; object-fit: cover; }
.explanation-modal__title { font-weight: 700; font-size: 18px; margin-bottom: 12px; }
.explanation-modal__text { white-space: pre-wrap; line-height: 1.6; }
@media all and (max-width: 1100px) { .test-layout, .test-result { grid-template-columns: 1fr; } .test-sidebar { position: static; } }
@media all and (max-width: 900px) { .test-hero { grid-template-columns: 1fr; padding: 24px; } .test-hero__title { font-size: 30px; } .test-card__body, .compare-row { grid-template-columns: 1fr; } }
@media all and (max-width: 640px) { .test-sidebar__submit { display: none; } .test-form__mobile-submit { display: inline-flex; justify-content: center; } .test-card { padding: 20px; border-radius: 22px; } .test-card__title { font-size: 24px; } .test-card__head { flex-direction: column; } }
</style>
